import { createSelector } from 'reselect'
import type { Contact, RecipientList, RecipientListId } from 'types/list'
import type { State } from 'types/redux'
import { SORT_DIRECTION_ASC } from '@disco/constants/SortTypes'
import type { State as ContactsAllState } from '@disco/state-management/reducers/entities/contactsAll'
import {
  getCampaign,
  getCampaignRules,
} from '@disco/state-management/selectors/campaigns'
import {
  getEmailGroups,
  getLiveEmailGroups,
} from '@disco/state-management/selectors/emailGroups'
import { getContactStatus } from '@disco/utils/contacts'
type GetContactListProps = {
  list?: RecipientList | null | undefined
  appendingContacts?: boolean | null | undefined
}
/**
 * Gets the contacts all state.
 * @param state - The Redux state
 * @returns The contacts all state object
 */
export const getContactsAll = (state: State): ContactsAllState =>
  state.entities.contactsAll

/**
 * Gets the contacts sort parameter.
 * @param state - The Redux state
 * @returns The sort parameter string
 */
const getContactsSortParam = (state: State) => getContactsAll(state).sortParam

/**
 * Gets the contacts sort direction.
 * @param state - The Redux state
 * @returns The sort direction string
 */
const getContactsSortDirection = (state: State) =>
  getContactsAll(state).sortDirection

/**
 * Gets the contacts search term.
 * @param state - The Redux state
 * @returns The search term string
 */
const getSearchTerm = (state: State) => getContactsAll(state).searchTerm

/**
 * Gets contacts list based on optional filtering criteria.
 * Can return all contacts, contacts in a specific list, or contacts to append to a list.
 * @param state - The Redux state
 * @param props - Optional props for filtering
 * @param props.list - The recipient list to filter by
 * @param props.appendingContacts - Whether to get contacts for appending to a list
 * @returns Array of filtered contacts
 */
export const getContactsList = createSelector(
  [
    getContactsAll,
    (_s, props?: GetContactListProps) => props && props.list,
    (_s, props?: GetContactListProps) => props && props.appendingContacts,
  ],
  (contactsState, list, appendingContacts) => {
    const allContacts = contactsState.contacts ?? []

    if (list) {
      const destListRecipients: number[] = []
      list.recipients.forEach(r => {
        if (r.contact) destListRecipients.push(r.contact.id)
      })

      if (appendingContacts) {
        // mode: add existing - append contacts to a list
        return allContacts
          .filter(
            c =>
              !(
                destListRecipients.includes(c.id) ||
                list.recipients.find(r => r.email === c.email)
              ),
          )
          .filter(c => c.email)
      } else {
        // mode: contacts in mailing list
        return allContacts.filter(
          c =>
            destListRecipients.includes(c.id) ||
            list.recipients.find(r => r.email === c.email),
        )
      }
    } else {
      // mode: all contacts
      return allContacts
    }
  },
)

/**
 * Sorts and filters contacts based on parameters and search term.
 * @param contacts - Array of contact objects
 * @param list - The recipient list object
 * @param sortParam - The parameter to sort by
 * @param sortDirection - The sort direction (ASC/DESC)
 * @param searchTerm - The search term to filter by
 * @returns Sorted and filtered array of contacts
 */
const sortedContacts = (
  contacts: Contact[],
  list,
  sortParam,
  sortDirection,
  searchTerm: string,
) => {
  const lessThan = sortDirection === SORT_DIRECTION_ASC ? -1 : 1
  const moreThan = sortDirection === SORT_DIRECTION_ASC ? 1 : -1

  const getContactAttr = contact => {
    if (sortParam === 'Status') {
      return getContactStatus(contact, list && list.id)
    }

    const contactParamValue = contact[sortParam.toLowerCase()]
    return (
      Array.isArray(contactParamValue)
        ? contactParamValue.join(', ')
        : contactParamValue || ''
    ).trim()
  }

  const searchTerms = searchTerm
    ? searchTerm.toLowerCase().trim().replace(/\s+/gi, ' ').split(' ')
    : []
  const filteredContacts =
    searchTerms.length > 0
      ? contacts.slice().filter(c => {
          if (!c) return false
          let customDataString = ''

          if (c.custom_data && Object.keys(c.custom_data).length > 0) {
            customDataString = Object.values(c.custom_data)
              .filter(v => typeof v === 'string' && v.trim().length > 0)
              .join(' ')
          }

          const doc = `${c.name} ${c.email} ${c.phone} ${c.company} ${
            c.territory
          } ${customDataString} ${(c.tags || []).join(', ')}`.toLowerCase()
          return !searchTerms.some(term => !doc.includes(term))
        })
      : contacts.slice()
  return filteredContacts.sort((a, b) => {
    const aParam = getContactAttr(a)
    const bParam = getContactAttr(b)
    if (!aParam && bParam) return 1
    if (aParam && !bParam) return -1
    if (aParam.toLowerCase() < bParam.toLowerCase()) return lessThan
    if (aParam.toLowerCase() > bParam.toLowerCase()) return moreThan
    return 0
  })
}

/**
 * Gets filtered and sorted contacts excluding deleted ones.
 * @param state - The Redux state
 * @param props - Optional props for filtering
 * @returns Array of filtered, sorted, and visible contacts
 */
export const getFilteredContacts = createSelector(
  [
    getContactsList,
    (_s, props?: GetContactListProps) => props && props.list,
    getContactsSortParam,
    getContactsSortDirection,
    getSearchTerm,
  ],
  (contacts, list, sortParam, sortDirection, searchTerm) => {
    const visibleContacts = contacts.filter(c => !c.deleted)
    return sortedContacts(
      visibleContacts,
      list,
      sortParam,
      sortDirection,
      searchTerm ?? '',
    )
  },
)

/**
 * Filters contacts to only include those who are subscribed (not globally unsubscribed).
 * @param contacts - Array of contact objects
 * @returns Array of subscribed contacts
 */
const getSubscribedContacts = (contacts: Contact[]) => {
  return contacts.filter(c => {
    return !(c.unsubscribes && c.unsubscribes.global)
  })
}

/**
 * Checks if a contact is subscribed to a specific email group.
 * @param c - The contact object
 * @param targetEmailGroup - The email group to check subscription for
 * @returns True if contact is subscribed to the group, false otherwise
 */
const getSubscriptionStatusOfContactInEmailGroup = (
  c: Contact,
  targetEmailGroup: RecipientList | undefined,
) => {
  if (!targetEmailGroup) return false

  const contactBelongsToGroup = targetEmailGroup.recipients.find(
    r => r.contact && (r.contact.id === c.id || r.email === c.email),
  )
  const contactUnsubscribedFromGroup =
    c.unsubscribes &&
    c.unsubscribes.saved_recipients_ids.includes(targetEmailGroup.id)
  return !!(contactBelongsToGroup && !contactUnsubscribedFromGroup)
}

/**
 * Gets subscribed contacts for a specific email group.
 * @param state - The Redux state
 * @param props - Props containing the group ID
 * @param props.groupId - The email group ID to get subscribed contacts for
 * @returns Array of contacts subscribed to the specified email group
 */
export const getSubscribedContactsInEmailGroup = createSelector(
  [
    getContactsList,
    getEmailGroups,
    (_s, props: { groupId: RecipientListId }) => props.groupId,
  ],
  (contacts, emailGroups, mailingGroupId) => {
    const targetEmailGroup = emailGroups.find(g => g.id === mailingGroupId)

    const subscribedContacts = getSubscribedContacts(contacts)
    return subscribedContacts.filter(c => {
      const res = getSubscriptionStatusOfContactInEmailGroup(
        c,
        targetEmailGroup,
      )
      return res
    })
  },
)

/**
 * Gets contacts filtered by campaign recipient rules.
 * Filters contacts based on mailing list subscriptions, tags, and other criteria.
 * @param state - The Redux state
 * @returns Object containing filtered contacts and associated mailing lists
 */
export const getContactsFilteredByCampaignRules = createSelector(
  [getFilteredContacts, getCampaignRules, getLiveEmailGroups],
  (contacts, campaignRules, emailGroups) => {
    const associatedLists = {}
    const subscribedContacts = getSubscribedContacts(contacts)
    const filteredContacts = subscribedContacts.filter(c => {
      for (const rule of campaignRules.rules) {
        let res = true
        let lvalue
        let rvalue = rule.ruleValue
        let op

        if (rule.ruleType === 'mailingList') {
          // handle mailingList rule separately as it's wildly differs
          // from the the rest of the rule types
          const mailingGroupId = parseInt(rule.ruleValue)
          const targetEmailGroup = emailGroups.find(
            g => g.id === mailingGroupId,
          )

          res = getSubscriptionStatusOfContactInEmailGroup(c, targetEmailGroup)

          if (res) associatedLists[c.id] = mailingGroupId
        } else if (
          // for backwards compatibility handle type rule as tags rule
          (rule.ruleType === 'tags' || rule.ruleType === 'type') &&
          rvalue
        ) {
          lvalue = c['tags']

          res = lvalue.includes(rvalue)
          res = rule.ruleOp === 'equals' ? res : !res
          if (res && campaignRules.matchMode === 'any') return true
          if (!res && campaignRules.matchMode === 'all') return false
        } else if (rvalue) {
          lvalue = (c[rule.ruleType] || '').toLowerCase().trim()
          rvalue = rvalue.toLowerCase().trim()
          op = rule.ruleOp

          switch (op) {
            case 'contains':
              res = lvalue.includes(`${rvalue}`)
              break

            case 'notContains':
              res = !lvalue.includes(`${rvalue}`)
              break

            case 'equals':
              res = lvalue === rvalue
              break

            case 'notEquals':
              res = lvalue !== rvalue
              break
          }
        }

        if (res && campaignRules.matchMode === 'any') return true
        if (!res && campaignRules.matchMode === 'all') return false
      }

      if (campaignRules.matchMode === 'any') {
        // normally false, except if there was zero rules
        return campaignRules.rules.length === 0
      }

      if (campaignRules.matchMode === 'all') return true
    })
    return {
      filteredContacts,
      associatedLists,
    }
  },
)
/**
 * Gets the first contact from a campaign's recipients list.
 * @param state - The Redux state
 * @param campaignId - The campaign ID
 * @returns The first contact from the campaign, or null if no recipients
 */
export const getFirstContactFromCampaign = createSelector(
  [getCampaign, getContactsAll],
  (campaign, contactsState) => {
    if (campaign.recipients_list.length === 0) return null
    const randomContact = (contactsState.contacts ?? []).find(
      c => c.id === campaign.recipients_list[0]?.id,
    )
    return randomContact
  },
)
/**
 * Checks if all contacts have been loaded.
 * @param state - The Redux state
 * @returns True if contacts are fully loaded, false otherwise
 */
export const isContactsLoaded: (state: State) => boolean = createSelector(
  [getContactsAll],
  contactsState => {
    const loaded =
      contactsState.loaded && !contactsState.loading && !contactsState.next
    return !!loaded
  },
)
